import {
  createTenant,
  createTenantSuperadmin,
  getTenant,
  listTenantSuperadmins,
  listTenants,
  mintTenantSuperadminLink,
  retireTenant,
  rotateTenantBuildKey,
  updateTenant,
} from '@t4s/api-client'
import type {
  CreateTenantRequest,
  CreateTenantResponse,
  ListTenantsParams,
  MintedAccountLinkResponse,
  RetireTenantResponse,
  Tenant,
  TenantBuildKey,
  TenantPage,
  TenantSuperadmin,
  UpdateTenantRequest,
} from '@t4s/api-client/model'
import { unwrap } from '../lib/api-error.ts'

/**
 * The nine generated calls tenant management is made of.
 *
 * Every one is a **generated** client function (`packages/api-client`), so the request
 * shape, the response shape and every documented status come from the OpenAPI document
 * rather than from anything written here. Nothing in this application calls `fetch`
 * (`src/lib/gateway.ts` says the same about the session's five).
 *
 * The indirection exists for the same reason the session's does: a screen imports a
 * function with a name and a return type, and `unwrap` is the single place a generated
 * result becomes data or an `ApiFailure`.
 *
 * **`rotateBuildKeyById` replaces P03's four signing-key calls** (`mintBuildIdentifier`,
 * `listBuildIdentifierKeys`, `rotateBuildIdentifierKey`, `revokeBuildIdentifierKey`), all
 * gone with the signing-key model itself. Rotating a tenant's build key is the entire
 * surface: it is destructive (§19), tenant-scoped, and its own confirmation states the
 * consequence — no separate list, no separate revoke, because there is nothing
 * platform-wide left to list or revoke.
 */

export function fetchTenants(params: ListTenantsParams): Promise<TenantPage> {
  return unwrap<TenantPage>(listTenants(params))
}

export function fetchTenant(tenantId: string): Promise<Tenant> {
  return unwrap<Tenant>(getTenant(tenantId))
}

/**
 * Creates the tenant, its first superadmin and their activation link — one call.
 *
 * §8.4: three calls from a browser have two partial failures, and two of them produce a
 * company that exists and cannot be entered. **The response carries the link value, and
 * it is a mutation rather than a query on purpose** (§10.4): a mutation's result is read
 * once into component state and is never written to the query cache.
 */
export function createTenantWithSuperadmin(
  request: CreateTenantRequest,
): Promise<CreateTenantResponse> {
  return unwrap<CreateTenantResponse>(createTenant(request))
}

export function saveTenantSettings(tenantId: string, patch: UpdateTenantRequest): Promise<Tenant> {
  return unwrap<Tenant>(updateTenant(tenantId, patch))
}

export function retireTenantById(tenantId: string): Promise<RetireTenantResponse> {
  return unwrap<RetireTenantResponse>(retireTenant(tenantId))
}

/**
 * Rotates a tenant's build key — the kill switch for its builds. **Destructive** (§19):
 * every build carrying the old value stops working from its very next request. Confirmed
 * on screen, on the tenant's own page.
 */
export function rotateBuildKeyById(tenantId: string): Promise<TenantBuildKey> {
  return unwrap<TenantBuildKey>(rotateTenantBuildKey(tenantId))
}

export function fetchSuperadmins(tenantId: string): Promise<TenantSuperadmin[]> {
  return unwrap<TenantSuperadmin[]>(listTenantSuperadmins(tenantId))
}

/** Creates a superadmin in an existing tenant — §11.3's recovery, and §8.4's. */
export function createSuperadmin(
  tenantId: string,
  request: { nickname: string; fullName: string },
): Promise<MintedAccountLinkResponse> {
  return unwrap<MintedAccountLinkResponse>(createTenantSuperadmin(tenantId, request))
}

/** Mints a fresh link, revoking that account's outstanding one. Confirmed on screen. */
export function mintSuperadminLink(
  tenantId: string,
  userId: string,
): Promise<MintedAccountLinkResponse> {
  return unwrap<MintedAccountLinkResponse>(mintTenantSuperadminLink(tenantId, userId))
}

/**
 * The query keys this feature caches under.
 *
 * **Nothing here ever holds a link.** The two calls that mint one are mutations, and a
 * mutation's result is not cached (§10.4) — so no key below can be read to recover a
 * credential, by this application or by anything looking at its memory.
 */
export const TENANTS_QUERY_KEY = ['platform', 'tenants'] as const
export const tenantQueryKey = (tenantId: string) => ['platform', 'tenant', tenantId] as const
export const superadminsQueryKey = (tenantId: string) =>
  ['platform', 'tenant', tenantId, 'superadmins'] as const
